/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { DashboardType } from 'jotai/dashboard/DashboardObject';
import { indyDefaultVisualizations, VisualizationType } from 'jotai/visualization';
import { strategyVisualizations } from 'jotai/visualization/VisualizationType';
import { SeriesType } from 'models/viewModels';

export enum AuthRoles {
  GM = 'general-motors',
  ADMIN = 'admin',
  FeaturePreview = 'feature-preview',
  REPLAYADMIN = 'replay-admin',
  CombinedViewOnly = 'combined-view-only',
  Cup = 'series-user-w',
  Xfinity = 'series-user-b',
  Truck = 'series-user-c',
  Indy = 'series-user-o',
  F1 = 'series-user-f',
  Wec = 'series-user-we',
  Imsa = 'series-user-im',
  Strategy = 'dashboard-strategy',
}

/**
 * Defines the visualizations that can be access by a particular role.
 * TODO make this as a map of a set of roles to a set of visualizations to enable compound roles like GM-Indy, GM-Indy-Feature-Preview, etc,
 * define this mapping in database and retrieve via API call to avoid limitations/headaches due to equality of ES6 Sets
 */
export const roleVisualizationsMap: Map<AuthRoles, Set<VisualizationType>> = new Map<AuthRoles, Set<VisualizationType>>(
  [
    [AuthRoles.Indy, indyDefaultVisualizations],
    [AuthRoles.Strategy, strategyVisualizations],
  ]
);

// DashboardsTypes dictate what roles can be used to authorize visualizations
export const dashboardRolesMap = new Map<DashboardType, Set<AuthRoles>>([
  [DashboardType.IndyCar, new Set<AuthRoles>([AuthRoles.Indy])],
]);

export const nascarRoles = new Set<AuthRoles>([AuthRoles.Cup, AuthRoles.Xfinity, AuthRoles.Truck]);
export const cupRoles = new Set<AuthRoles>([AuthRoles.Cup]);
export const xfinityRoles = new Set<AuthRoles>([AuthRoles.Xfinity]);
export const truckRoles = new Set<AuthRoles>([AuthRoles.Truck]);
export const indyRoles = new Set<AuthRoles>([AuthRoles.Indy]);
export const f1Roles = new Set<AuthRoles>([AuthRoles.F1]);

export const seriesRolesMap = new Map<String, Set<AuthRoles>>([
  [SeriesType.CUP.toString(), cupRoles],
  [SeriesType.XFINITY.toString(), xfinityRoles],
  [SeriesType.TRUCK.toString(), truckRoles],
  [SeriesType.IndyCar.toString(), indyRoles],
  [SeriesType.Formula1.toString(), f1Roles],
]);

export const seriesRolesArray = [...nascarRoles, ...indyRoles, ...f1Roles];

// currently no mapped visualizations i.e. all visualizations are authorized for general use
export const restrictedVisualizationRoles = new Set<AuthRoles>([AuthRoles.FeaturePreview]);
